import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  History,
  Plus,
  Sparkles,
  Trash2,
  Wand2,
} from "lucide-react";
import { Sheet } from "./ui/Sheet";
import { Segmented } from "./ui/Segmented";
import { Button } from "./ui/Button";
import { useDesign } from "../lib/store";
import {
  additionKey,
  AiAnonymousQuotaExceededError,
  AiQuotaExceededError,
  buildApplyPayload,
  FENG_SHUI_NOTES_MAX_LENGTH,
  FengShuiInvalidLayoutError,
  FengShuiPremiumRequiredError,
  FengShuiSignInRequiredError,
  FengShuiUnavailableError,
  mountAdditionKey,
  requestFengShuiLayout,
  requestFengShuiRanking,
  type FengShuiInvalidLayoutItem,
  type FengShuiMode,
  type FengShuiRanking,
  type FengShuiSuggestion,
} from "../lib/fengShui";
import { hasAccount, useAuth } from "../lib/auth";
import { requestSavePrompt } from "../lib/savePromptRequests";
import type { SignInSurface, UpsellTrigger } from "../lib/events";
import { track } from "../lib/telemetry";
import { usePremium } from "../lib/premium";
import { quotaErrorMessageForPremiumClient } from "../lib/usage";
import { UsageBudgetWarning } from "./UsageMeter";

/** "Wall A · 24 in from start" formatting used by mount rows. */
function wallLabel(wallIndex: number): string {
  return `Wall ${String.fromCharCode(65 + wallIndex)}`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Open the consultation history sheet (deep-link target for "View past
   *  layouts"). Called with no args — the host decides which entry to
   *  expand. Omit to hide the history link entirely. */
  onOpenHistory?: () => void;
  /** Invoked when the backend rejects with `permission-denied` — the
   *  caller is signed in but isn't Premium. The host (DesignView) closes
   *  the sheet and pops the upsell overlay. */
  onPremiumRequired?: (trigger: UpsellTrigger) => void;
  /** Sign-in surface for the save prompt after an anonymous allowance
   *  error. The host owns the sign-in sheet itself. */
  signInSurface?: SignInSurface;
  /** The session cannot spend a credit (signed out, anonymous, or the
   *  server rejected the call). The host closes this sheet and opens
   *  sign-in. */
  onSignInRequired?: () => void;
}

type SheetTab = "generate" | "rank";

/** Signed-out and anonymous sessions have no account, so they cannot spend
 *  a credit. Loading and "Firebase unavailable" stay off this gate. */
function needsAccount(
  status: string,
  user: Parameters<typeof hasAccount>[0]
): boolean {
  return (
    status === "signed-out" || (status === "signed-in" && !hasAccount(user))
  );
}

/**
 * Sheet that hosts the two AI feng shui flows side-by-side:
 *  - **Generate** — propose a rearrangement / additions / removals.
 *  - **Rank** — score the current layout against 8 classical criteria.
 *
 * Both flows share the same `ai_layout_feng_shui` flag, the same Firebase
 * pre-flight (auth + invalid-layout), and the same inline alert patterns,
 * but maintain independent local state so switching tabs doesn't clobber
 * an in-flight request or a previously surfaced result.
 */
export function FengShuiSheet({
  open,
  onClose,
  onOpenHistory,
  onPremiumRequired,
  signInSurface = "ai_sheet",
  onSignInRequired,
}: Props) {
  const auth = useAuth();
  const [tab, setTab] = useState<SheetTab>("generate");
  // No account: the host shows a sign-in sheet instead. Rendering nothing
  // here keeps the generation form from sitting under that prompt.
  if (needsAccount(auth.status, auth.user)) return null;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Feng Shui layout"
      side="right"
      width="min(440px, 96vw)"
    >
      <div className="pt-1">
        <Segmented
          value={tab}
          onChange={(v) => setTab(v)}
          options={[
            { id: "generate", label: "Generate" },
            { id: "rank", label: "Rank" },
          ]}
          size="sm"
          className="mb-4"
        />
      </div>
      {tab === "generate" ? (
        <GenerateTab
          onClose={onClose}
          onOpenHistory={onOpenHistory}
          onPremiumRequired={onPremiumRequired}
          onSignInRequired={onSignInRequired}
          signInSurface={signInSurface}
        />
      ) : (
        <RankTab
          onClose={onClose}
          onOpenHistory={onOpenHistory}
          onPremiumRequired={onPremiumRequired}
          onSignInRequired={onSignInRequired}
          signInSurface={signInSurface}
        />
      )}
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Generate tab — original body, now scoped to its own component.
// ---------------------------------------------------------------------------

function GenerateTab({
  onClose,
  onOpenHistory,
  onPremiumRequired,
  onSignInRequired,
  signInSurface,
}: {
  onClose: () => void;
  onOpenHistory?: () => void;
  onPremiumRequired?: (trigger: UpsellTrigger) => void;
  onSignInRequired?: () => void;
  signInSurface: SignInSurface;
}) {
  const design = useDesign((s) => s.design);
  const applyFengShuiSuggestion = useDesign((s) => s.applyFengShuiSuggestion);
  const pastConsultationCount = design.consultations?.length ?? 0;

  const [mode, setMode] = useState<FengShuiMode>("rearrange");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | {
        kind: "invalid";
        items: FengShuiInvalidLayoutItem[];
      }
    | { kind: "ready"; suggestion: FengShuiSuggestion }
  >({ kind: "idle" });
  const [pickedUpdates, setPickedUpdates] = useState<Set<string>>(new Set());
  const [pickedAdditions, setPickedAdditions] = useState<Set<string>>(
    new Set()
  );
  const [pickedRemovals, setPickedRemovals] = useState<Set<string>>(new Set());
  const [pickedMountUpdates, setPickedMountUpdates] = useState<Set<string>>(
    new Set()
  );
  const [pickedMountAdditions, setPickedMountAdditions] = useState<Set<string>>(
    new Set()
  );
  const [pickedMountRemovals, setPickedMountRemovals] = useState<Set<string>>(
    new Set()
  );
  const premium = usePremium();
  // Mode of a generated layout that is on screen but not yet applied.
  // Cleared on apply; anything else that throws it away is a discard.
  const unappliedMode = useRef<FengShuiMode | null>(null);
  useEffect(
    () => () => {
      if (unappliedMode.current)
        track("ai_layout_discarded", { mode: unappliedMode.current });
    },
    []
  );

  async function generate() {
    setStatus({ kind: "loading" });
    try {
      const raw = await requestFengShuiLayout(design, mode, { notes });
      // Defensive normalize: an older Cloud Function deployment (or a test
      // mock) may return a response without the mount arrays; treat them
      // as empty so the UI never crashes mid-render.
      const suggestion: FengShuiSuggestion = {
        ...raw,
        mountUpdates: raw.mountUpdates ?? [],
        mountAdditions: raw.mountAdditions ?? [],
        mountRemovals: raw.mountRemovals ?? [],
      };
      // Default to picking everything the model returned — it's the most
      // useful starting point and easy to deselect items the user dislikes.
      setPickedUpdates(new Set(suggestion.updates.map((u) => u.id)));
      setPickedAdditions(new Set(suggestion.additions.map(additionKey)));
      setPickedRemovals(new Set(suggestion.removals.map((r) => r.id)));
      setPickedMountUpdates(new Set(suggestion.mountUpdates.map((u) => u.id)));
      setPickedMountAdditions(
        new Set(suggestion.mountAdditions.map(mountAdditionKey))
      );
      setPickedMountRemovals(
        new Set(suggestion.mountRemovals.map((r) => r.id))
      );
      unappliedMode.current = mode;
      setStatus({ kind: "ready", suggestion });
    } catch (err) {
      if (err instanceof FengShuiInvalidLayoutError) {
        setStatus({ kind: "invalid", items: err.items });
        return;
      }
      if (err instanceof FengShuiSignInRequiredError) {
        setStatus({ kind: "idle" });
        onSignInRequired?.();
        return;
      }
      if (err instanceof AiAnonymousQuotaExceededError) {
        // The anonymous taste is spent: an account brings the monthly
        // allowance, so this is the save prompt, not the paywall. Close
        // this sheet first so the two don't stack.
        setStatus({ kind: "idle" });
        onClose();
        requestSavePrompt({ surface: signInSurface });
        return;
      }
      if (
        err instanceof FengShuiPremiumRequiredError ||
        err instanceof AiQuotaExceededError
      ) {
        // Free quota exhausted (post-migration) OR legacy permission
        // gate. Keep the sheet open when the client already thinks it's
        // Premium — the upsell overlay auto-closes for isPremium.
        if (premium.isPremium) {
          setStatus({
            kind: "error",
            message: quotaErrorMessageForPremiumClient(premium),
          });
          return;
        }
        setStatus({ kind: "idle" });
        onPremiumRequired?.(
          err instanceof AiQuotaExceededError ? "quota" : "premium_required"
        );
        return;
      }
      const message =
        err instanceof FengShuiUnavailableError
          ? err.message
          : err instanceof Error
          ? err.message
          : "Something went wrong.";
      setStatus({ kind: "error", message });
    }
  }

  function reset() {
    if (unappliedMode.current) {
      track("ai_layout_discarded", { mode: unappliedMode.current });
      unappliedMode.current = null;
    }
    setStatus({ kind: "idle" });
    setPickedUpdates(new Set());
    setPickedAdditions(new Set());
    setPickedRemovals(new Set());
    setPickedMountUpdates(new Set());
    setPickedMountAdditions(new Set());
    setPickedMountRemovals(new Set());
  }

  function apply() {
    if (status.kind !== "ready") return;
    const payload = buildApplyPayload(
      status.suggestion,
      {
        updateIds: pickedUpdates,
        additionKeys: pickedAdditions,
        removalIds: pickedRemovals,
        mountUpdateIds: pickedMountUpdates,
        mountAdditionKeys: pickedMountAdditions,
        mountRemovalIds: pickedMountRemovals,
      },
      { mode, notes }
    );
    unappliedMode.current = null;
    applyFengShuiSuggestion(payload);
    reset();
    onClose();
  }

  const furnitureById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of design.furniture) m.set(f.id, f.name);
    return m;
  }, [design.furniture]);

  const mountById = useMemo(() => {
    const m = new Map<string, string>();
    for (const x of design.mounts) m.set(x.id, x.name);
    return m;
  }, [design.mounts]);

  return (
    <>
      {status.kind !== "ready" && (
        <div className="pt-1">
          <p className="text-sm text-ink-500">
            Generates a furniture arrangement based on classical feng shui —
            command position, qi flow, balance — given your room shape,
            openings, and pieces.
          </p>
          {onOpenHistory && pastConsultationCount > 0 && (
            <button
              type="button"
              onClick={onOpenHistory}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-ink-700 hover:text-ink-900 underline underline-offset-2 decoration-ink-300 hover:decoration-ink-700"
              data-testid="feng-shui-view-past-layouts"
            >
              <History className="size-3.5" />
              View past layouts ({pastConsultationCount})
            </button>
          )}
          <div className="mt-5">
            <div className="text-xs font-medium text-ink-500 mb-2">
              Approach
            </div>
            <Segmented
              value={mode}
              onChange={(v) => setMode(v)}
              options={[
                { id: "rearrange", label: "Rearrange" },
                { id: "rearrange_suggest", label: "Rearrange + suggest" },
                { id: "redesign", label: "Full redesign" },
              ]}
              size="sm"
            />
            <p className="text-caption text-ink-400 mt-2">
              {mode === "rearrange" &&
                "Move and rotate the furniture and wall mounts you already have. No additions, no removals."}
              {mode === "rearrange_suggest" &&
                "Rearrange existing pieces and suggest up to 3 furniture additions and up to 3 wall-mount additions from the catalogs."}
              {mode === "redesign" &&
                "Full freedom: rearrange, add up to 6 furniture pieces and 6 wall mounts, remove up to 4 of each."}
            </p>
          </div>

          <div className="mt-5">
            <label
              htmlFor="feng-shui-notes"
              className="text-xs font-medium text-ink-500 mb-2 flex items-center justify-between"
            >
              <span>
                Additional requirements{" "}
                <span className="font-normal text-ink-400">(optional)</span>
              </span>
              <span className="text-label text-ink-400 tabular-nums">
                {notes.length}/{FENG_SHUI_NOTES_MAX_LENGTH}
              </span>
            </label>
            <textarea
              id="feng-shui-notes"
              data-testid="feng-shui-notes"
              value={notes}
              onChange={(e) =>
                setNotes(e.target.value.slice(0, FENG_SHUI_NOTES_MAX_LENGTH))
              }
              maxLength={FENG_SHUI_NOTES_MAX_LENGTH}
              rows={3}
              placeholder={
                'e.g. "The loveseat folds out into a bed at night, leave 36\u201D in front of it."'
              }
              className="w-full resize-y rounded-xl bg-white ring-1 ring-ink-200 px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-ink-900/30 disabled:opacity-60"
              disabled={status.kind === "loading"}
            />
            <p className="text-caption text-ink-400 mt-1.5">
              These sit alongside classical feng shui. Hard constraints (fit,
              door swing, and so on) still apply.
            </p>
          </div>

          {status.kind === "error" && (
            <div
              className="mt-4 p-3 rounded-xl bg-rose-50 ring-1 ring-rose-200 text-sm text-rose-800"
              data-testid="feng-shui-error"
            >
              {status.message}
            </div>
          )}

          {status.kind === "invalid" && (
            <InvalidLayoutAlert items={status.items} />
          )}

          <UsageBudgetWarning />

          <div className="mt-6 flex flex-col gap-2">
            <Button
              variant="primary"
              size="lg"
              onClick={generate}
              disabled={
                status.kind === "loading" ||
                design.furniture.length + design.mounts.length === 0
              }
            >
              {status.kind === "loading" ? (
                <>
                  <Spinner />
                  Consulting the bagua…
                </>
              ) : (
                <>
                  <Wand2 className="size-4" />
                  Generate layout
                </>
              )}
            </Button>
            {design.furniture.length + design.mounts.length === 0 && (
              <div className="text-xs text-ink-400 text-center">
                Add some furniture or wall mounts first, then come back.
              </div>
            )}
          </div>
        </div>
      )}

      {status.kind === "ready" &&
        (() => {
          // Defensive read: a mock or older function deployment may produce a
          // suggestion without the mount arrays. The production wire path runs
          // through `normalizeSuggestion`, but tests mock the wrapper directly.
          const sg = {
            ...status.suggestion,
            mountUpdates: status.suggestion.mountUpdates ?? [],
            mountAdditions: status.suggestion.mountAdditions ?? [],
            mountRemovals: status.suggestion.mountRemovals ?? [],
          };
          return (
            <div className="pt-1">
              {(() => {
                const f = sg.filtered;
                const total =
                  (f?.updates ?? 0) +
                  (f?.additions ?? 0) +
                  (f?.mountUpdates ?? 0) +
                  (f?.mountAdditions ?? 0);
                if (total === 0) return null;
                return (
                  <div
                    className="mb-3 p-3 rounded-xl bg-amber-50 ring-1 ring-amber-200 text-caption text-amber-900 flex items-start gap-2"
                    data-testid="feng-shui-filtered-notice"
                  >
                    <AlertTriangle className="size-4 mt-0.5 shrink-0 text-amber-600" />
                    <span>
                      {total} suggestion{total === 1 ? "" : "s"} didn&apos;t fit
                      your room and {total === 1 ? "was" : "were"} skipped.
                    </span>
                  </div>
                );
              })()}
              <div className="rounded-2xl bg-ink-900 text-white p-4">
                <div className="flex items-center gap-1.5 text-caption uppercase font-semibold text-ink-300">
                  <Sparkles className="size-3.5" />
                  Overall
                </div>
                <div className="text-sm mt-1 leading-snug">
                  {status.suggestion.overall}
                </div>
              </div>

              {status.suggestion.updates.length > 0 && (
                <section className="mt-5">
                  <div className="text-xs font-semibold text-ink-500 mb-2 flex items-center gap-2">
                    <ArrowRight className="size-3.5" />
                    Move ({status.suggestion.updates.length})
                  </div>
                  <ul className="flex flex-col gap-1.5">
                    {status.suggestion.updates.map((u) => (
                      <SuggestionRow
                        key={u.id}
                        title={furnitureById.get(u.id) ?? u.id}
                        detail={`(${Math.round(u.x)}, ${Math.round(
                          u.y
                        )}) · ${Math.round(u.rotation)}°`}
                        reason={u.reason}
                        checked={pickedUpdates.has(u.id)}
                        onChange={(c) =>
                          setPickedUpdates((s) => toggle(s, u.id, c))
                        }
                      />
                    ))}
                  </ul>
                </section>
              )}

              {status.suggestion.additions.length > 0 && (
                <section className="mt-5">
                  <div className="text-xs font-semibold text-ink-500 mb-2 flex items-center gap-2">
                    <Plus className="size-3.5" />
                    Add ({status.suggestion.additions.length})
                  </div>
                  <ul className="flex flex-col gap-1.5">
                    {status.suggestion.additions.map((a) => {
                      const key = additionKey(a);
                      return (
                        <SuggestionRow
                          key={key}
                          title={a.presetName}
                          detail={`(${Math.round(a.x)}, ${Math.round(
                            a.y
                          )}) · ${Math.round(a.rotation)}°`}
                          reason={a.reason}
                          checked={pickedAdditions.has(key)}
                          onChange={(c) =>
                            setPickedAdditions((s) => toggle(s, key, c))
                          }
                        />
                      );
                    })}
                  </ul>
                </section>
              )}

              {status.suggestion.removals.length > 0 && (
                <section className="mt-5">
                  <div className="text-xs font-semibold text-ink-500 mb-2 flex items-center gap-2">
                    <Trash2 className="size-3.5" />
                    Remove ({status.suggestion.removals.length})
                  </div>
                  <ul className="flex flex-col gap-1.5">
                    {status.suggestion.removals.map((r) => (
                      <SuggestionRow
                        key={r.id}
                        title={furnitureById.get(r.id) ?? r.id}
                        detail="will be removed"
                        reason={r.reason}
                        checked={pickedRemovals.has(r.id)}
                        onChange={(c) =>
                          setPickedRemovals((s) => toggle(s, r.id, c))
                        }
                        variant="danger"
                      />
                    ))}
                  </ul>
                </section>
              )}

              {sg.mountUpdates.length > 0 && (
                <section className="mt-5" data-testid="feng-shui-mount-updates">
                  <div className="text-xs font-semibold text-ink-500 mb-2 flex items-center gap-2">
                    <ArrowRight className="size-3.5" />
                    Move on wall ({sg.mountUpdates.length})
                  </div>
                  <ul className="flex flex-col gap-1.5">
                    {sg.mountUpdates.map((u) => (
                      <SuggestionRow
                        key={u.id}
                        title={mountById.get(u.id) ?? u.id}
                        detail={`${wallLabel(u.wallIndex)} · ${Math.round(
                          u.alongWall
                        )}\u201D from start · ${Math.round(
                          u.fromFloor
                        )}\u201D up`}
                        reason={u.reason}
                        checked={pickedMountUpdates.has(u.id)}
                        onChange={(c) =>
                          setPickedMountUpdates((s) => toggle(s, u.id, c))
                        }
                      />
                    ))}
                  </ul>
                </section>
              )}

              {sg.mountAdditions.length > 0 && (
                <section
                  className="mt-5"
                  data-testid="feng-shui-mount-additions"
                >
                  <div className="text-xs font-semibold text-ink-500 mb-2 flex items-center gap-2">
                    <Plus className="size-3.5" />
                    Hang on wall ({sg.mountAdditions.length})
                  </div>
                  <ul className="flex flex-col gap-1.5">
                    {sg.mountAdditions.map((a) => {
                      const key = mountAdditionKey(a);
                      return (
                        <SuggestionRow
                          key={key}
                          title={a.presetName}
                          detail={`${wallLabel(a.wallIndex)} · ${Math.round(
                            a.alongWall
                          )}\u201D from start · ${Math.round(
                            a.fromFloor
                          )}\u201D up`}
                          reason={a.reason}
                          checked={pickedMountAdditions.has(key)}
                          onChange={(c) =>
                            setPickedMountAdditions((s) => toggle(s, key, c))
                          }
                        />
                      );
                    })}
                  </ul>
                </section>
              )}

              {sg.mountRemovals.length > 0 && (
                <section
                  className="mt-5"
                  data-testid="feng-shui-mount-removals"
                >
                  <div className="text-xs font-semibold text-ink-500 mb-2 flex items-center gap-2">
                    <Trash2 className="size-3.5" />
                    Take off wall ({sg.mountRemovals.length})
                  </div>
                  <ul className="flex flex-col gap-1.5">
                    {sg.mountRemovals.map((r) => (
                      <SuggestionRow
                        key={r.id}
                        title={mountById.get(r.id) ?? r.id}
                        detail="will be removed"
                        reason={r.reason}
                        checked={pickedMountRemovals.has(r.id)}
                        onChange={(c) =>
                          setPickedMountRemovals((s) => toggle(s, r.id, c))
                        }
                        variant="danger"
                      />
                    ))}
                  </ul>
                </section>
              )}

              <div className="mt-6 flex items-center gap-2 sticky bottom-0 -mx-5 px-5 py-3 bg-white border-t border-ink-100">
                <Button variant="ghost" size="md" onClick={reset}>
                  Try again
                </Button>
                <div className="flex-1" />
                <Button
                  variant="primary"
                  size="md"
                  onClick={apply}
                  disabled={
                    pickedUpdates.size +
                      pickedAdditions.size +
                      pickedRemovals.size +
                      pickedMountUpdates.size +
                      pickedMountAdditions.size +
                      pickedMountRemovals.size ===
                    0
                  }
                >
                  Apply selected
                </Button>
              </div>
            </div>
          );
        })()}
    </>
  );
}

// ---------------------------------------------------------------------------
// Rank tab — score the current layout against the 8 classical criteria.
// ---------------------------------------------------------------------------

type RankStatus =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "invalid"; items: FengShuiInvalidLayoutItem[] }
  | { kind: "ready"; ranking: FengShuiRanking };

function RankTab({
  onClose,
  onOpenHistory,
  onPremiumRequired,
  onSignInRequired,
  signInSurface,
}: {
  onClose: () => void;
  onOpenHistory?: () => void;
  onPremiumRequired?: (trigger: UpsellTrigger) => void;
  onSignInRequired?: () => void;
  signInSurface: SignInSurface;
}) {
  const design = useDesign((s) => s.design);
  const appendRanking = useDesign((s) => s.appendRanking);
  const pastRankingCount = design.rankings?.length ?? 0;

  const [status, setStatus] = useState<RankStatus>({ kind: "idle" });
  const premium = usePremium();

  async function rank() {
    setStatus({ kind: "loading" });
    try {
      const ranking = await requestFengShuiRanking(design);
      appendRanking(ranking);
      setStatus({ kind: "ready", ranking });
    } catch (err) {
      if (err instanceof FengShuiSignInRequiredError) {
        setStatus({ kind: "idle" });
        onSignInRequired?.();
        return;
      }
      if (err instanceof AiAnonymousQuotaExceededError) {
        // Same handoff as Generate: the save prompt replaces this sheet.
        setStatus({ kind: "idle" });
        onClose();
        requestSavePrompt({ surface: signInSurface });
        return;
      }
      if (
        err instanceof FengShuiPremiumRequiredError ||
        err instanceof AiQuotaExceededError
      ) {
        if (premium.isPremium) {
          setStatus({
            kind: "error",
            message: quotaErrorMessageForPremiumClient(premium),
          });
          return;
        }
        setStatus({ kind: "idle" });
        onPremiumRequired?.(
          err instanceof AiQuotaExceededError ? "quota" : "premium_required"
        );
        return;
      }
      setStatus(toRankErrorStatus(err));
    }
  }

  if (status.kind === "ready") {
    return <RankResultView ranking={status.ranking} onReRank={rank} />;
  }

  return (
    <div className="pt-1">
      <p className="text-sm text-ink-500">
        Scores your current layout against eight classical feng shui criteria —
        commanding position, qi flow, bagua, five elements, doors &amp; windows,
        sha chi, wall mounts, and clearance.
      </p>
      {onOpenHistory && pastRankingCount > 0 && (
        <button
          type="button"
          onClick={onOpenHistory}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-ink-700 hover:text-ink-900 underline underline-offset-2 decoration-ink-300 hover:decoration-ink-700"
          data-testid="feng-shui-view-past-rankings"
        >
          <History className="size-3.5" />
          View past rankings ({pastRankingCount})
        </button>
      )}

      {status.kind === "error" && (
        <div
          className="mt-4 p-3 rounded-xl bg-rose-50 ring-1 ring-rose-200 text-sm text-rose-800"
          data-testid="feng-shui-error"
        >
          {status.message}
        </div>
      )}

      {status.kind === "invalid" && <InvalidLayoutAlert items={status.items} />}

      <UsageBudgetWarning />

      <div className="mt-6 flex flex-col gap-2">
        <Button
          variant="primary"
          size="lg"
          onClick={rank}
          disabled={
            status.kind === "loading" ||
            design.furniture.length + design.mounts.length === 0
          }
          data-testid="feng-shui-rank-button"
        >
          {status.kind === "loading" ? (
            <>
              <Spinner />
              Consulting the bagua…
            </>
          ) : (
            <>
              <Sparkles className="size-4" />
              Rank my layout
            </>
          )}
        </Button>
        {design.furniture.length + design.mounts.length === 0 && (
          <div className="text-xs text-ink-400 text-center">
            Add some furniture or wall mounts first, then come back.
          </div>
        )}
      </div>
    </div>
  );
}

function RankResultView({
  ranking,
  onReRank,
}: {
  ranking: FengShuiRanking;
  onReRank: () => void;
}) {
  return (
    <div className="pt-1" data-testid="feng-shui-rank-result">
      <div className="rounded-2xl bg-ink-900 text-white p-4 flex items-center gap-4">
        <ScoreDial score={ranking.overall.score} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-caption uppercase font-semibold text-ink-300">
            <Sparkles className="size-3.5" />
            Overall
          </div>
          <p className="text-sm mt-1 leading-snug">{ranking.overall.summary}</p>
        </div>
      </div>

      <ul className="mt-5 flex flex-col gap-2.5">
        {ranking.criteria.map((c) => (
          <li
            key={c.id}
            className="rounded-2xl bg-white ring-1 ring-ink-200 p-3"
            data-testid={`feng-shui-criterion-${c.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <div className="text-body-sm font-semibold text-ink-900 truncate">
                    {c.label}
                  </div>
                  <div className="text-label text-ink-400 uppercase tracking-wide shrink-0">
                    weight {c.weight}
                  </div>
                </div>
                <div className="mt-2">
                  <ScoreBar score={c.score} />
                </div>
                {c.rationale && (
                  <p className="text-caption text-ink-600 mt-2 leading-snug">
                    {c.rationale}
                  </p>
                )}
                {c.observations.length > 0 && (
                  <ul className="mt-2 flex flex-col gap-0.5 pl-4 list-disc marker:text-ink-300 text-caption text-ink-600">
                    {c.observations.map((o, i) => (
                      <li key={i}>{o}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-2xl font-bold text-ink-900 tabular-nums leading-none">
                  {Math.round(c.score)}
                </div>
                <div className="text-label uppercase text-ink-400 tracking-wide">
                  / 100
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex items-center gap-2 sticky bottom-0 -mx-5 px-5 py-3 bg-white border-t border-ink-100">
        <div className="flex-1" />
        <Button
          variant="primary"
          size="md"
          onClick={onReRank}
          data-testid="feng-shui-rerank-button"
        >
          <Sparkles className="size-4" />
          Re-rank
        </Button>
      </div>
    </div>
  );
}

function ScoreDial({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, score));
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const dash = (clamped / 100) * circumference;
  return (
    <div className="relative shrink-0">
      <svg
        width={72}
        height={72}
        viewBox="0 0 72 72"
        className="block"
        aria-hidden
      >
        <circle
          cx={36}
          cy={36}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth={6}
        />
        <circle
          cx={36}
          cy={36}
          r={radius}
          fill="none"
          stroke="white"
          strokeWidth={6}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform="rotate(-90 36 36)"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-lg font-bold tabular-nums">
        {Math.round(clamped)}
      </div>
    </div>
  );
}

function ScoreBar({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <div
      className="h-1.5 w-full rounded-full bg-ink-100 overflow-hidden"
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full bg-ink-900 rounded-full"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

/** Reusable "your layout has pieces that don't fit" inline alert. */
function InvalidLayoutAlert({ items }: { items: FengShuiInvalidLayoutItem[] }) {
  return (
    <div
      className="mt-4 p-3 rounded-xl bg-rose-50 ring-1 ring-rose-200 text-sm text-rose-800"
      data-testid="feng-shui-invalid"
    >
      <div className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="size-4" />
        {invalidLayoutHeading(items)}
      </div>
      <p className="mt-1 text-rose-700/90">
        Fix these before generating a layout:
      </p>
      <ul className="mt-2 flex flex-col gap-0.5 pl-4 list-disc marker:text-rose-300">
        {items.map((it) => (
          <li key={`${it.kind}:${it.id}`}>
            {it.name}
            {it.kind === "mount" && (
              <span className="text-rose-500/80"> (wall mount)</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Map an exception from `requestFengShuiRanking` into the RankTab's
 *  status union so the inline alerts re-use the same patterns as Generate. */
function toRankErrorStatus(err: unknown): RankStatus {
  if (err instanceof FengShuiInvalidLayoutError) {
    return { kind: "invalid", items: err.items };
  }
  const message =
    err instanceof FengShuiUnavailableError
      ? err.message
      : err instanceof Error
      ? err.message
      : "Something went wrong.";
  return { kind: "error", message };
}

function toggle<T>(set: Set<T>, key: T, checked: boolean): Set<T> {
  const next = new Set(set);
  if (checked) next.add(key);
  else next.delete(key);
  return next;
}

/** Pick a heading for the invalid-layout error sheet that matches what the
 *  user actually has out of place. Keeps the existing copy when only floor
 *  furniture is offending so existing tests/screenshots don't drift. */
function invalidLayoutHeading(items: FengShuiInvalidLayoutItem[]): string {
  const hasFurniture = items.some((it) => it.kind === "furniture");
  const hasMount = items.some((it) => it.kind === "mount");
  if (hasFurniture && hasMount) return "Some pieces are out of place";
  if (hasMount) return "Some wall mounts don't fit";
  return "Some furniture is outside the room";
}

function SuggestionRow({
  title,
  detail,
  reason,
  checked,
  onChange,
  variant = "default",
}: {
  title: string;
  detail: string;
  reason: string;
  checked: boolean;
  onChange: (c: boolean) => void;
  variant?: "default" | "danger";
}) {
  return (
    <li
      className={`flex gap-3 p-3 rounded-xl ring-1 ${
        variant === "danger"
          ? "bg-rose-50/60 ring-rose-200"
          : "bg-ink-50 ring-ink-100"
      }`}
    >
      <label className="flex items-start gap-3 cursor-pointer flex-1 min-w-0">
        <input
          type="checkbox"
          className="mt-1 size-4 accent-ink-900 shrink-0"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <div className="text-body-sm font-semibold text-ink-900 truncate">
              {title}
            </div>
            <div className="text-caption text-ink-400 shrink-0">{detail}</div>
          </div>
          <div className="text-caption text-ink-500 mt-1 leading-snug">
            {reason}
          </div>
        </div>
      </label>
    </li>
  );
}

function Spinner() {
  return (
    <span className="inline-block size-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
  );
}
